import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { CalendarEventBookingProtocol } from './event-booking-protocol/calendar-event-booking-protocol.js';
import { EventBookingProtocol } from './event-booking-protocol/event-booking-protocol.js';
import { TicketAsPassEventBookingProtocol } from './event-booking-protocol/ticket-as-pass-event-booking-protocol.js';
import { TicketsOnlyEventBookingProtocol } from './event-booking-protocol/tickets-only-event-booking-protocol.js';
import { VouchersOnlyEventBookingProtocol } from './event-booking-protocol/vouchers-only-event-booking-protocol.js';
import { LegacyTicketBookingStrategy } from './legacy-ticket-booking-strategy.js';

export class LegacyTicketBookingSettings extends DataModel {
  static readonly TYPE_ID: string = '4f15c35d21984983a61c3f8687361975';
  static readonly SCHEMA_VERSION: number = 4;

  private constructor(
    public readonly strategy: LegacyTicketBookingStrategy,
    public readonly shouldSendElectronicPass: boolean,
    public readonly shouldSendOnlyPaymentConfirmation: boolean,
    public readonly allowExtendedCartSize: boolean,
    public readonly deferOrderCompletion: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return LegacyTicketBookingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacyTicketBookingSettings.SCHEMA_VERSION);
  }

  static create(args: {
    strategy: LegacyTicketBookingStrategy,
    shouldSendElectronicPass: boolean,
    shouldSendOnlyPaymentConfirmation: boolean,
    allowExtendedCartSize: boolean,
    deferOrderCompletion: boolean,
  }): LegacyTicketBookingSettings {
    return new LegacyTicketBookingSettings(
        args.strategy,
        args.shouldSendElectronicPass,
        args.shouldSendOnlyPaymentConfirmation,
        args.allowExtendedCartSize,
        args.deferOrderCompletion,
    );
  }

  static standard(): LegacyTicketBookingSettings {
    return new LegacyTicketBookingSettings(
        LegacyTicketBookingStrategy.Standard,
        false, false, false, false
    );
  }

  static calendarBooking(): LegacyTicketBookingSettings {
    return new LegacyTicketBookingSettings(
        LegacyTicketBookingStrategy.CalendarBooking,
        false, false, false, false,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyTicketBookingSettings {
    return new LegacyTicketBookingSettings(
        toEnumFromString(MapUtils.getString(map, 'strategy'), LegacyTicketBookingStrategy),
        MapUtils.get<boolean>(map, 'shouldSendElectronicPass'),
        MapUtils.get<boolean>(map, 'shouldSendOnlyPaymentConfirmation'),
        MapUtils.get<boolean>(map, 'allowExtendedCartSize'),
        MapUtils.get<boolean>(map, 'deferOrderCompletion'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'strategy': fromEnumAsString(this.strategy),
      'shouldSendElectronicPass': this.shouldSendElectronicPass,
      'shouldSendOnlyPaymentConfirmation': this.shouldSendOnlyPaymentConfirmation,
      'allowExtendedCartSize': this.allowExtendedCartSize,
      'deferOrderCompletion': this.deferOrderCompletion,
    };
  }

  clone(): this {
    return new LegacyTicketBookingSettings(
        this.strategy,
        this.shouldSendElectronicPass,
        this.shouldSendOnlyPaymentConfirmation,
        this.allowExtendedCartSize,
        this.deferOrderCompletion,
    ) as this;
  }

  isSame(other: LegacyTicketBookingSettings): boolean {
    if(this === other) {
      return true;
    }

    return other.strategy === this.strategy &&
      other.shouldSendElectronicPass === this.shouldSendElectronicPass &&
      other.shouldSendOnlyPaymentConfirmation === this.shouldSendOnlyPaymentConfirmation &&
      other.allowExtendedCartSize === this.allowExtendedCartSize &&
      other.deferOrderCompletion === this.deferOrderCompletion;
  }

  toEventBookingProtocol(opts: { hasProductList: boolean }): EventBookingProtocol {
    switch(this.strategy) {
      case LegacyTicketBookingStrategy.Standard:
        return opts.hasProductList ?
          VouchersOnlyEventBookingProtocol.create():
          TicketsOnlyEventBookingProtocol.create({
            sendOnlyPaymentConfirmation: this.shouldSendOnlyPaymentConfirmation,
            allowExtendedCartSize: this.allowExtendedCartSize,
            deferOrderCompletion: this.deferOrderCompletion,
          });
      case LegacyTicketBookingStrategy.CalendarBooking:
        return CalendarEventBookingProtocol.create();
      case LegacyTicketBookingStrategy.TicketAsOpenAccessPass:
        return TicketAsPassEventBookingProtocol.legacy({
          shouldSendElectronicPass: this.shouldSendElectronicPass,
        });
    }
  }
}

export class LegacyTicketBookingSettingsBuilder extends DataModelBuilder<LegacyTicketBookingSettings> {
  build(context: SerializationContext, map: SerializedData): LegacyTicketBookingSettings {
    return LegacyTicketBookingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnum(map, 'strategy', LegacyTicketBookingStrategy, LegacyTicketBookingStrategy.Standard);

    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LegacyTicketBookingSettings.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(LegacyTicketBookingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      map['shouldSendElectronicPass'] = true;
      version = 2;
    }

    if(version < 3) {
      map['deferOrderCompletion'] = false;
      version = 3;
    }

    if(version < 4) {
      map['shouldSendOnlyPaymentConfirmation'] = false;
      map['allowExtendedCartSize'] = false;
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
